import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { LogOut, Home, Save } from 'lucide-react';

const AdminContact = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
    fetchContent();
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
    }
  };

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/contact-content', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setContent(data);
      } else if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('İçerik yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
    toast.success('Çıkış yapıldı');
  };

  const handleChange = (field, value) => {
    setContent({ ...content, [field]: value });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/contact-content', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(content),
      });

      if (response.ok) {
        toast.success('İçerik kaydedildi');
      } else {
        toast.error('Kaydetme başarısız');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-[#1e4a7a]">MİGA Yapı - Admin Panel</h1>
            <div className="flex items-center space-x-4">
              <Link to="/" target="_blank">
                <Button variant="outline" size="sm">
                  <Home className="h-4 w-4 mr-2" />
                  Siteyi Görüntüle
                </Button>
              </Link>
              <Button onClick={handleLogout} variant="outline" size="sm">
                <LogOut className="h-4 w-4 mr-2" />
                Çıkış
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 py-4">
            <Link to="/admin/dashboard" className="text-gray-600 hover:text-[#1e4a7a] pb-4">
              Ana Sayfa İçeriği
            </Link>
            <Link to="/admin/about" className="text-gray-600 hover:text-[#1e4a7a] pb-4">
              Kurumsal
            </Link>
            <Link to="/admin/services" className="text-gray-600 hover:text-[#1e4a7a] pb-4">
              Hizmetlerimiz
            </Link>
            <Link to="/admin/contact" className="text-[#1e4a7a] font-medium border-b-2 border-[#1e4a7a] pb-4">
            <Link to="/admin/faq" className="text-gray-600 hover:text-[#1e4a7a] pb-4 whitespace-nowrap">
              SSS
            </Link>
              İletişim
            <Link to="/admin/faq" className="text-gray-600 hover:text-[#1e4a7a] pb-4 whitespace-nowrap">
              SSS
            </Link>
            </Link>
            <Link to="/admin/projects" className="text-gray-600 hover:text-[#1e4a7a] pb-4">
              Proje Yönetimi
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">İletişim Sayfa İçeriği</h2>
          </div>

          <form onSubmit={handleSave} className="space-y-6">
            {/* Header Section */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold mb-4">Başlık Bölümü</h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label>Sayfa Başlığı</Label>
                  <Input
                    value={content?.title || ''}
                    onChange={(e) => handleChange('title', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Alt Başlık</Label>
                  <Textarea
                    value={content?.subtitle || ''}
                    onChange={(e) => handleChange('subtitle', e.target.value)}
                    rows={2}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold mb-4">İletişim Bilgileri</h3>
              <div className="space-y-4">
                <div>
                  <Label>Adres</Label>
                  <Input
                    value={content?.address || ''}
                    onChange={(e) => handleChange('address', e.target.value)}
                    required
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Telefon</Label>
                    <Input
                      value={content?.phone || ''}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      placeholder="0 (212) 433 01 48"
                      required
                    />
                  </div>
                  <div>
                    <Label>GSM (Opsiyonel)</Label>
                    <Input
                      value={content?.mobile || ''}
                      onChange={(e) => handleChange('mobile', e.target.value)}
                      placeholder="0 (555) 123 45 67"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={content?.email || ''}
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder="info@migayapi.com"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold mb-4">Harita</h3>
              <div>
                <Label>Google Maps Embed URL</Label>
                <Textarea
                  value={content?.map_embed_url || ''}
                  onChange={(e) => handleChange('map_embed_url', e.target.value)}
                  rows={3}
                  placeholder="https://www.google.com/maps/embed?pb=..."
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Google Maps'ten "Paylaş" → "Harita ekle" → Embed kodu alın
                </p>
              </div>
            </div>

            {/* CTA Section */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Teklif Alma Bölümü (CTA)</h3>
              <div className="space-y-4">
                <div>
                  <Label>CTA Başlık</Label>
                  <Input
                    value={content?.cta_title || ''}
                    onChange={(e) => handleChange('cta_title', e.target.value)}
                    placeholder="Ücretsiz Teklif Almak İster misiniz?"
                    required
                  />
                </div>
                <div>
                  <Label>CTA Alt Başlık</Label>
                  <Input
                    value={content?.cta_subtitle || ''}
                    onChange={(e) => handleChange('cta_subtitle', e.target.value)}
                    placeholder="Tapu belgenizi yükleyerek hemen ücretsiz ve detaylı teklif alabilirsiniz"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Working Hours */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold mb-4">Çalışma Saatleri</h3>
              <div className="space-y-4">
                <div>
                  <Label>Hafta İçi (Pazartesi - Cuma)</Label>
                  <Input
                    value={content?.working_hours_weekday || ''}
                    onChange={(e) => handleChange('working_hours_weekday', e.target.value)}
                    placeholder="Pazartesi - Cuma: 09:00 - 18:00"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Cumartesi</Label>
                  <Input
                    value={content?.working_hours_saturday || ''}
                    onChange={(e) => handleChange('working_hours_saturday', e.target.value)}
                    placeholder="Cumartesi: 10:00 - 13:00"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Pazar</Label>
                  <Input
                    value={content?.working_hours_sunday || ''}
                    onChange={(e) => handleChange('working_hours_sunday', e.target.value)}
                    placeholder="Pazar: Kapalı"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-end pt-6 border-t">
              <Button type="submit" disabled={saving} className="bg-[#dc2626] hover:bg-[#b91c1c]">
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default AdminContact;
